import React, { useState, useEffect } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { getData } from "../../Services/getData";
import { urlEnum } from "../../Utils/urlEnum";
import { Vocabulary } from "../../Utils/Vocabulary";

export interface ClientData {
  id: number;
  nume: string;
  [key: string]: any;
}

export interface ClientDropdownProps {
  value?: ClientData | null;
  onChange: (client: ClientData | null) => void;
  size?: "small" | "medium";
  fullWidth?: boolean;
  label?: string;
  variant?: "outlined" | "filled" | "standard";
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  helperText?: string;
}

const ClientDropdown: React.FC<ClientDropdownProps> = ({
  value = null,
  onChange,
  size = "small",
  fullWidth = true,
  label = Vocabulary.client,
  variant = "outlined",
  disabled = false,
  required = false,
  error = false,
  helperText,
}) => {
  const [clients, setClients] = useState<ClientData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchClients();
  }, []);

  /**
   * Fetches the list of clients from the server, and updates the local state
   * @returns {Promise<void>}
   */
  const fetchClients = async () => {
    setLoading(true);
    try {
      const response = await getData(urlEnum.clients);
      setClients(response.data || []);
    } catch (error) {
      console.error("Error fetching clients:", error);
      setClients([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Renders the client dropdown component
   */
  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      disabled={disabled || loading}
      value={value}
      onChange={(event, newValue) => onChange(newValue)}
      options={clients}
      getOptionLabel={(option) => option?.nume || ""}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          variant={variant}
          required={required}
          error={error}
          helperText={helperText}
        />
      )}
      noOptionsText={loading ? "Loading..." : "No clients found"}
    />
  );
};

export default ClientDropdown;
